import React, { useEffect, useState } from "react";
import "../App.css";
import Nav from "./Navigation.js";
import { getTokenInternal } from "./useToken";
import { useNavigate } from "react-router-dom";
import LoginRequired from "./LoginRequired.js";


export default function MyProfile() {
  const [myself, setMyself] = useState({});
  const [token, setToken] = useState();
  const [visible, setVisible] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [languageId, setLanguageId] = useState(1);
  const [games, setGames] = useState([]);
  const [allRequirements, setAllRequirements] = useState([]);
  const [myRequirementIds, setMyRequirementIds] = useState([]);
  const [hideUnobtainable, setHideUnobtainable] = useState(false);
  const [apiKeyActive, setApiKeyActive] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const navigate = useNavigate();


  useEffect(() => {
    const fetchToken = async () => {
      try {
        const fetchedToken = await getTokenInternal();
        setToken(fetchedToken);
      } catch (error) {
        console.error("Error fetching token:", error);
      } finally {
        setTokenChecked(true);
      }
    };
    fetchToken();
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_HOST}/api/languages`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setLanguages)
      .catch((err) => console.error("Error fetching languages:", err.message));
  }, []);

  useEffect(() => {
    const fetchEntitlements = async () => {
      const [gamesRes, reqRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_HOST}/api/games`),
        fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements`),
      ]);
      if (gamesRes.ok) setGames(await gamesRes.json());
      if (reqRes.ok) setAllRequirements(await reqRes.json());
    };
    fetchEntitlements();
  }, []);

  useEffect(() => {
    const fetchMyReqs = async () => {
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/my-requirements`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (res.ok) setMyRequirementIds(await res.json());
    };
    fetchMyReqs();
  }, [token]);

  const handleToggleRequirement = async (reqId, currentlyOwned) => {
    const method = currentlyOwned ? "DELETE" : "POST";
    const res = await fetch(
      `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${reqId}/claim-mine`,
      { method, headers: { Authorization: `Bearer ${token.access_token}` } }
    );
    if (res.ok) {
      setMyRequirementIds((prev) =>
        currentlyOwned ? prev.filter((id) => id !== reqId) : [...prev, reqId]
      );
    }
  };

  const fetchData = async () => {
    if (token) {
      const url = `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}`;
      const response = await fetch(url);
      const completeList = await response.json();
      setMyself(completeList);
      setVisible(completeList.visible || false);
      setLanguageId(completeList.language_id || 1);
      setHideUnobtainable(completeList.hide_unobtainable || false);

      const keyStatusRes = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/api-key/status`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (keyStatusRes.ok) {
        const keyStatus = await keyStatusRes.json();
        setApiKeyActive(keyStatus.is_active);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const handleLanguageChange = async (event) => {
    const newLanguageId = Number(event.target.value);
    setLanguageId(newLanguageId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/language`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token?.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ language_id: newLanguageId }),
        }
      );
      if (!response.ok) {
        throw new Error("Error updating language.");
      }
    } catch (error) {
      console.error("Error updating language:", error);
    }
  };

const handleToggle = () => {
  // Update the visible status
  setVisible(!visible);

  fetch(
    `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token?.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ visible: !visible }),
    }
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error("Error updating account.");
      }
      return response.json();
    })
    .then((data) => {
    })
    .catch((error) => {
      console.error("Error updating account:", error);
    });
};

const handleGenerateApiKey = async () => {
  const res = await fetch(
    `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/api-key`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token.access_token}` },
    }
  );
  if (res.ok) {
    const data = await res.json();
    setNewApiKey(data.key);
    setApiKeyActive(true);
    setApiKeyCopied(false);
  }
};

const handleDeactivateApiKey = async () => {
  const res = await fetch(
    `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/api-key`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token.access_token}` },
    }
  );
  if (res.ok) {
    setApiKeyActive(false);
    setNewApiKey(null);
  }
};

const handleDeleteAccount = async () => {
  try {
    const url = `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token?.account.username)}`;
    const response = await fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token.access_token}`,
      },
    });

    if (response.ok) {
      navigate("/", { state: { notice: "Your account has been deleted." } });
    } else {
      console.error("Failed to delete account:", response.statusText);
    }
  } catch (error) {
    console.error("Error deleting account:", error);
  }
};

  if (tokenChecked && !token) return <LoginRequired />;

  return (
    <div>
      <Nav />
      <div className="text-white text-center">
        <div>
          <h1 className="text-3xl font-bold mb-4">My Profile Settings</h1>
          <div>
            <span className="mr-2">Username:</span>
            <span>{myself.username}</span>
          </div>
          <div>
            <span className="mr-2">Discord Name:</span>
            <span>{myself.discord_name}</span>
          </div>
          <div className="mt-4">
            <span className="mr-2">Discord Name Visible To Friends:</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={visible}
                onChange={handleToggle}
              />
              <span className="slider"></span>
            </label>
          </div>
          <div className="mt-4">
            <span className="mr-2">Language:</span>
            <select
              value={languageId}
              onChange={handleLanguageChange}
              className="p-1 border rounded text-black"
            >
              {languages.map((language) => (
                <option key={language.id} value={language.id}>
                  {language.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-10">
            <button
              className="mr-2 p-1 border rounded"
              onClick={() => {
                navigate(`/edit-my-account/${myself.id}`, {
                  state: { myself },
                });
              }}
            >
              Change My Account Name or Password
            </button>
          </div>

          {/* Entitlements — what the user owns (DLC, preorders, etc.) */}
          {allRequirements.length > 0 && (
            <div className="mt-10 max-w-md mx-auto text-left">
              <h2 className="text-xl font-bold mb-1 text-center">My Purchases & Entitlements</h2>
              <p className="text-sm text-gray-300 mb-4 text-center">
                Check off anything you own. Items you don't have will be grayed out in the tracker and excluded from your count.
              </p>
              {games.map((game) => {
                const gameReqs = allRequirements.filter((r) => r.games_id === game.id);
                if (gameReqs.length === 0) return null;
                return (
                  <div key={game.id} className="mb-6">
                    <h3 className="font-bold text-lg mb-2 border-b border-gray-600 pb-1">{game.name}</h3>
                    {gameReqs.map((req) => {
                      const owned = myRequirementIds.includes(req.id);
                      return (
                        <label key={req.id} className="flex items-start gap-2 mb-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={owned}
                            onChange={() => handleToggleRequirement(req.id, owned)}
                            className="mt-1 h-4 w-4 shrink-0"
                          />
                          <div>
                            <div className="font-medium">
                              {req.name}
                              {req.platform ? ` (${req.platform})` : ""}
                            </div>
                            <div className="text-xs text-gray-400">
                              {req.is_available
                                ? req.requirement_type === "dlc"
                                  ? "Still available for purchase"
                                  : "Still available"
                                : req.requirement_type === "preorder"
                                ? "No longer available — preorder only"
                                : req.requirement_type === "early access"
                                ? "No longer available — early access only"
                                : "No longer available"}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6">
            <span className="mr-2">Hide unobtainable items in My List:</span>
            <label className="switch">
              <input
                type="checkbox"
                checked={hideUnobtainable}
                onChange={async () => {
                  const next = !hideUnobtainable;
                  setHideUnobtainable(next);
                  await fetch(
                    `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/hide-unobtainable`,
                    {
                      method: "PUT",
                      headers: {
                        Authorization: `Bearer ${token.access_token}`,
                        "Content-Type": "application/json",
                      },
                      body: JSON.stringify({ hide_unobtainable: next }),
                    }
                  );
                }}
              />
              <span className="slider"></span>
            </label>
          </div>

          <div className="mt-10 max-w-md mx-auto text-left border border-gray-600 rounded-lg p-4">
            <h2 className="text-xl font-bold mb-1 text-center">Mod API Key</h2>
            <p className="text-sm text-gray-300 mb-4 text-center">
              Use this key in your mod config instead of your login credentials. Keep it private.
            </p>
            {newApiKey ? (
              <div className="mb-3">
                <p className="text-yellow-400 text-sm mb-2 font-semibold">Save this key now — it will not be shown again.</p>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={newApiKey}
                    className="flex-1 text-black text-xs p-1 rounded font-mono"
                  />
                  <button
                    className="p-1 border rounded text-sm shrink-0"
                    onClick={() => {
                      navigator.clipboard.writeText(newApiKey);
                      setApiKeyCopied(true);
                    }}
                  >
                    {apiKeyCopied ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 mb-3 text-center">
                Status: <span className={apiKeyActive ? "text-green-400 font-semibold" : "text-gray-500"}>{apiKeyActive ? "Active" : "No active key"}</span>
              </p>
            )}
            <div className="flex justify-center gap-2">
              <button className="p-1 border rounded text-sm" onClick={handleGenerateApiKey}>
                {apiKeyActive ? "Regenerate Key" : "Generate Key"}
              </button>
              {apiKeyActive && (
                <button className="p-1 border rounded text-sm text-red-400" onClick={handleDeactivateApiKey}>
                  Deactivate Key
                </button>
              )}
            </div>
          </div>

          <div className="mt-10">
            <button
              type="button"
              className="ml-2 bg-red-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
              onClick={() => handleDeleteAccount()}
            >
                Delete My Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
